import { useContext, useEffect, useState } from 'react';

import { getSocialConnections } from '../../api';
import { SocialConnectionsResponse } from '../../api/configurations/social/types';
import { Error } from '../../api/setup/getApiError';
import { SettingsContext } from './context';
import { CurrentSocialsProps } from './types';

// Module-level cache to track in-flight requests (persists across hook instances)
const inFlightRequests = new Map<string, Promise<SocialConnectionsResponse>>();

export const useSocialConnections = (
  loginProjectId?: string,
  getNewData: boolean = true
): CurrentSocialsProps => {
  const { currentSocials, setCurrentSocials } = useContext(SettingsContext);
  const [fetching, setFetching] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!getNewData || !loginProjectId) {
      setFetching(false);
      return;
    }

    // Checking if there's already an in-flight request for this project
    const existingRequest = inFlightRequests.get(loginProjectId);
    if (existingRequest) {
      setFetching(true);
      existingRequest
        .then((res) => {
          const sortedSocials = res.sort((a, b) => a.provider.localeCompare(b.provider));
          setCurrentSocials(sortedSocials);
        })
        .catch((err) => setError(err))
        .finally(() => setFetching(false));
      return;
    }

    // Creating new request and caching it
    setFetching(true);
    const request = getSocialConnections(loginProjectId);
    inFlightRequests.set(loginProjectId, request);

    request
      .then((res) => {
        const sortedSocials = res.sort((a, b) => a.provider.localeCompare(b.provider));
        setCurrentSocials(sortedSocials);
      })
      .catch((err) => setError(err))
      .finally(() => {
        setFetching(false);
        inFlightRequests.delete(loginProjectId);
      });
  }, [loginProjectId, getNewData]);

  return {
    currentSocialsFetching: fetching,
    error,
    currentSocials,
    setCurrentSocials
  };
};
